import { DateTime, P } from '@piccolohealth/util';
import React from 'react';
import { FaRegCalendar } from 'react-icons/fa';
import * as ChakraV3 from './chakra/index';
import { LOCALE, parseZonedDateTimeSafe } from './RangeDatepicker';

export interface SingleDatepickerProps
  extends Omit<ChakraV3.DatePicker.RootProps, 'onChange' | 'value'> {
  value: DateTime | null;
  onChange: (date: DateTime | null) => void;
  isDisabled?: boolean;
  showIcon?: boolean;
}

export const SingleDatepicker = (props: SingleDatepickerProps) => {
  const { value, onChange, isDisabled, showIcon = true, css, ...rest } = props;

  const valuePrime = React.useMemo(() => {
    if (props.value) {
      return P.compact([parseZonedDateTimeSafe(props.value)]);
    }
    return [];
  }, [props.value]);

  const onChangePrime = React.useCallback(
    (details: ChakraV3.DatePicker.ValueChangeDetails) => {
      const [start] = details.value;

      if (P.isUndefined(start)) {
        props.onChange(null);
        return;
      }

      props.onChange(
        DateTime.fromISO(start.set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toString()),
      );
    },
    [props],
  );

  return (
    <ChakraV3.DatePicker.Root
      value={valuePrime}
      onValueChange={onChangePrime}
      disabled={props.isDisabled}
      timeZone={value?.zoneName}
      locale={LOCALE}
      openOnClick
      positioning={{ placement: 'bottom-start', strategy: 'fixed' }}
      lazyMount
      unmountOnExit
      {...rest}
    >
      <ChakraV3.DatePicker.Control>
        <ChakraV3.DatePicker.Input w='0' css={css} />
        <ChakraV3.Show when={showIcon}>
          <ChakraV3.DatePicker.IndicatorGroup>
            <ChakraV3.DatePicker.Context>
              {(context) =>
                context.value.length ? (
                  <ChakraV3.DatePicker.ClearTrigger />
                ) : (
                  <ChakraV3.DatePicker.Trigger>
                    <FaRegCalendar />
                  </ChakraV3.DatePicker.Trigger>
                )
              }
            </ChakraV3.DatePicker.Context>
          </ChakraV3.DatePicker.IndicatorGroup>
        </ChakraV3.Show>
      </ChakraV3.DatePicker.Control>
      <ChakraV3.DatePicker.Positioner>
        <ChakraV3.DatePicker.Content>
          <ChakraV3.DatePicker.View view='day' colorPalette='purple'>
            <ChakraV3.DatePicker.Header />
            <ChakraV3.DatePicker.DayTable />
          </ChakraV3.DatePicker.View>
          <ChakraV3.DatePicker.View view='month' colorPalette='purple'>
            <ChakraV3.DatePicker.Header />
            <ChakraV3.DatePicker.MonthTable />
          </ChakraV3.DatePicker.View>
          <ChakraV3.DatePicker.View view='year' colorPalette='purple'>
            <ChakraV3.DatePicker.Header />
            <ChakraV3.DatePicker.YearTable />
          </ChakraV3.DatePicker.View>
        </ChakraV3.DatePicker.Content>
      </ChakraV3.DatePicker.Positioner>
    </ChakraV3.DatePicker.Root>
  );
};
